import { type FC, type ReactNode, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Card,
  Checkbox,
  CircularProgress,
  Collapse,
  IconButton,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import copy from "copy-to-clipboard";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useLinksQuery, type LinksItem } from "@/partner/entities/links";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { ItemTitleLink } from "@/partner/shared/components/ItemTitleLink";
import { PagePaths } from "@/partner/shared/constants";
import { useDeviceBreakpoint } from "@/partner/shared/hooks";
import { formatMoney, formatNumber } from "@/partner/shared/utils/formatLocale";
import { LinkTypeType } from "@/types/graphql/partner.generated";
import { LinksListToolbar } from "./LinksListToolbar";
import { LinkRewardSummary } from "./LinkRewardSummary";
import { buildLinksFilter } from "../model/buildLinksFilter";
import {
  LINK_TARGET_OPTION_LABEL_KEYS,
  resolveLinkTargetOption,
} from "../model";
import {
  linkAnalyticsUrl,
  type LinkAnalyticsTab,
} from "../model/linkAnalyticsUrl";

interface LinksListProps {
  onEditLink: (id: string) => void;
}

/** Short link without the scheme: https://site.tv/l/abc -> site.tv/l/abc. */
const shortUrlLabel = (url: string) => url.replace(/^https?:\/\//, "");

export const LinksList: FC<LinksListProps> = ({ onEditLink }) => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useDeviceBreakpoint("md");

  const {
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
    filter,
  } = useFilter();

  const { data, loading } = useLinksQuery({
    variables: {
      page,
      rowsPerPage,
      filter: buildLinksFilter(filter),
    },
  });

  const links = data?.links.nodes ?? [];
  const count = data?.links.count ?? 0;

  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const numSelected = selectedIds.length;

  // stats: null у сотрудника без доступа к аналитике, тогда колонок цифр нет.
  const showStats = !isMobile && links.some((link) => link.stats);
  const showReward =
    showStats && links.some((link) => link.reward && link.stats);

  const handleToggleSelectAll = () => {
    if (numSelected === links.length) {
      setSelected({});
    } else {
      setSelected(Object.fromEntries(links.map((link) => [link.id, true])));
    }
  };

  const handleToggleSelectOne = (id: string) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCopy = (url: string) => {
    copy(url);
    enqueueSnackbar(t("links.item.urlCopiedSnackbar"), { variant: "success" });
  };

  const number = (value: number) => formatNumber(value, i18n.language);

  const analyticsLink = (
    link: LinksItem,
    tab: LinkAnalyticsTab,
    children: ReactNode
  ) => (
    <MuiLink
      component={RouterLink}
      to={linkAnalyticsUrl(tab, link.id, link.createdAt)}
      color="textPrimary"
      underline="hover"
    >
      {children}
    </MuiLink>
  );

  const targetLabel = (link: LinksItem): string => {
    if (link.linkType === LinkTypeType.ExternalType) {
      return link.href ?? t("common.emptyDash");
    }
    const option = resolveLinkTargetOption(link.targetType, link.targetUrl);
    const title = link.targetTitle ?? t("linkTarget.missing");
    return option
      ? t("linkTarget.label", {
          type: t(LINK_TARGET_OPTION_LABEL_KEYS[option]),
          title,
        })
      : title;
  };

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (links.length === 0) {
    return <EmptyListCard title={t("links.list.empty")} />;
  }

  return (
    <Card elevation={0}>
      <Collapse in={numSelected > 0}>
        <LinksListToolbar
          selectedIds={selectedIds}
          onCloseSelected={() => setSelected({})}
        />
      </Collapse>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                color="default"
                indeterminate={numSelected > 0 && numSelected < links.length}
                checked={links.length > 0 && numSelected === links.length}
                onChange={handleToggleSelectAll}
              />
            </TableCell>
            <TableCell>{t("links.list.nameColumn")}</TableCell>
            {showStats && (
              <>
                <TableCell align="right">
                  {t("links.list.clicksColumn")}
                </TableCell>
                <TableCell align="right">
                  {t("links.list.viewsColumn")}
                </TableCell>
                <TableCell align="right">
                  {t("links.list.registrationsColumn")}
                </TableCell>
                <TableCell align="right">
                  {t("links.list.salesColumn")}
                </TableCell>
                <TableCell align="right">
                  {t("links.list.revenueColumn")}
                </TableCell>
              </>
            )}
            {showReward && (
              <TableCell align="right">
                {t("links.list.rewardColumn")}
              </TableCell>
            )}
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {links.map((link) => {
            const { stats } = link;
            const money = (value: number) =>
              stats ? formatMoney(value, stats.currency, i18n.language) : "";

            return (
              <TableRow key={link.id} selected={Boolean(selected[link.id])}>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="default"
                    checked={Boolean(selected[link.id])}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => handleToggleSelectOne(link.id)}
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  <ItemTitleLink
                    to={`${PagePaths.links}?tab=edit&id=${link.id}`}
                    onOpen={() => onEditLink(link.id)}
                    color="textPrimary"
                    variant="h6"
                  >
                    {link.title}
                  </ItemTitleLink>
                  {link.url && (
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {shortUrlLabel(link.url)}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {targetLabel(link)}
                  </Typography>
                  <LinkRewardSummary link={link} />
                </TableCell>
                {showStats && (
                  <>
                    <TableCell align="right">
                      {stats ? number(stats.clicks) : t("common.emptyDash")}
                    </TableCell>
                    <TableCell align="right">
                      {stats
                        ? analyticsLink(link, "views", number(stats.views))
                        : t("common.emptyDash")}
                    </TableCell>
                    <TableCell align="right">
                      {stats
                        ? analyticsLink(
                            link,
                            "users",
                            number(stats.registrations)
                          )
                        : t("common.emptyDash")}
                    </TableCell>
                    <TableCell align="right">
                      {stats
                        ? analyticsLink(link, "sales", number(stats.sales))
                        : t("common.emptyDash")}
                    </TableCell>
                    <TableCell align="right">
                      {stats ? (
                        <Typography
                          component="span"
                          variant="body2"
                          noWrap
                          sx={
                            stats.revenue > 0
                              ? { color: "success.main", fontWeight: 700 }
                              : undefined
                          }
                        >
                          {money(stats.revenue)}
                        </Typography>
                      ) : (
                        t("common.emptyDash")
                      )}
                    </TableCell>
                  </>
                )}
                {showReward && (
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {link.reward && stats
                      ? money(stats.reward)
                      : t("common.emptyDash")}
                  </TableCell>
                )}
                <TableCell align="right">
                  {link.url && (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="flex-end"
                    >
                      <Tooltip title={t("links.item.copyAria")}>
                        <IconButton
                          onClick={() => handleCopy(link.url as string)}
                          aria-label={t("links.item.copyAria")}
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("links.item.openAria")}>
                        <IconButton
                          href={link.targetUrl || link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          component="a"
                          aria-label={t("links.item.openAria")}
                        >
                          <OpenInNewIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Card>
  );
};
